import crypto from 'crypto-js'
import Swagger from 'swagger-client'
import uuid from 'uuid/v4'

import * as utils from './utils'

/**
 * un-wrap swagger response
 * @param resp
 * @returns {*} response's content object
 */
function unwrap(
  resp = {
    obj: {
      content: {},
    },
  }
) {
  return resp.obj.content
}

let client
let url

async function init(
  opts = {
    url: '',
    server: { url: '' },
    requestInterceptor: (req) => {},
  }
) {
  url = opts.url
  return new Promise((resolve, reject) => {
    Swagger({
      url: opts.url,
      requestInterceptor: (req) => {
        if (opts.requestInterceptor) {
          opts.requestInterceptor(req)
        }
      },
    }).then(
      (_client) => {
        client = _client
        client.spec.servers = [opts.server]
        resolve(client)
      },
      (err) => {
        reject(err)
      }
    )
  })
}

const item = {
  /**
   * use item id to fetch info
   * @param {number} id item id
   */
  fetchItem: async (
    id,
    opts = {
      test: false,
      testParkEndTime: undefined,
    }
  ) => {
    let resp = await client.apis['item'].readItem({
      id,
      ...utils.snakelize(opts || {}),
    })
    return unwrap(resp)
  },

  /**
   * get items info by options
   * @param opts options for fetch items
   */
  fetchItems: async (
    opts = {
      page: 0,
      pageSize: 20,
      takeType: 'all',
      categoryId: null,
      type: null,
      sortDirection: null,
    }
  ) => {
    let resp = await client.apis['item'].readItems(utils.snakelize(opts))

    return resp.obj
  },

  /**
   * duplicate specified item
   * @param {*} item
   */
  duplicateItem: async (item) => {
    let resp = await client.apis['item'].addItem(
      {
        duplicate: true,
      },
      {
        requestBody: { id: item.id },
      }
    )
    return resp.obj.content
  },

  /**
   * delete specified item
   * @param {*} item
   */
  deleteItem: async (item) => {
    await client.apis['item'].deleteItem({ id: item.id })
  },

  /**
   * add item
   * @param {*} item
   */
  addItem: async (item, opts = {}) => {
    let resp = await client.apis['item'].addItem(opts, {
      requestBody: item,
    })
    return resp.obj.content
  },

  /**
   * update specified item
   * @param {*} item
   */
  updateItem: async (item) => {
    let resp = await client.apis['item'].updateItem(
      { id: item.id },
      {
        requestBody: item,
      }
    )
    return resp.obj.content
  },

  updateItemPicture: async (itemId, picture) => {
    await client.apis['item'].updateItemPicture(
      { id: itemId },
      {
        requestBody: {
          image: picture,
        },
      }
    )
  },
}

const category = {
  /**
   * fetch categorys
   * @param opts options for fetch categorys
   */
  fetchCategorys: async (
    opts = {
      page: 0,
      pageSize: 20,
    }
  ) => {
    let resp = await client.apis['item-category'].readItemCategorys({
      page: opts.page,
      page_size: opts.pageSize,
    })
    return resp.obj.content
  },

  fetchCategory: async (id) => {
    let resp = await client.apis['item-category'].readItemCategory({
      id: id,
    })
    return resp.obj.content
  },

  updateCategory: async (category) => {
    let resp = await client.apis['item-category'].updateItemCategory(
      {
        id: category.id,
      },
      {
        requestBody: category,
      }
    )
    return resp.obj.content
  },

  addCategory: async (category) => {
    let resp = await client.apis['item-category'].addItemCategory(
      {},
      {
        requestBody: category,
      }
    )
    return resp.obj.content
  },

  deleteCategory: async (id) => {
    let resp = await client.apis['item-category'].deleteItemCategory({
      id: id,
    })
    return resp.obj.content
  },

  updateCategoryPicture: async (cid, picture) => {
    await client.apis['item-category'].updateItemCategoryPicture(
      { id: cid },
      {
        requestBody: {
          image: picture,
        },
      }
    )
  },
}

const ingredient = {
  fetchIngredients: async (
    opts = {
      page: 0,
      pageSize: 20,
    }
  ) => {
    let resp = await client.apis['ingredient'].readIngredients({
      page: opts.page,
      page_size: opts.pageSize,
    })
    return resp.obj
  },

  fetchIngredient: async (id) => {
    let resp = await client.apis['ingredient'].readIngredient({ id: id })
    return resp.obj.content
  },

  addIngredient: async (ingredient) => {
    let resp = await client.apis['ingredient'].addIngredient(
      {},
      {
        requestBody: ingredient,
      }
    )
    return resp.obj.content
  },

  updateIngredient: async (ingredient) => {
    let resp = await client.apis['ingredient'].updateIngredient(
      { id: ingredient.id },
      {
        requestBody: ingredient,
      }
    )
    return resp.obj.content
  },

  deleteIngredient: async (id) => {
    let resp = await client.apis['ingredient'].deleteIngredient({ id: id })
    return resp.obj.content
  },
}

const app = {
  fetchApps: async (
    opts = {
      page: 0,
      pageSize: 20,
    }
  ) => {
    let resp = await client.apis['app'].readApps(utils.snakelize(opts))
    return resp.obj.content
  },

  fetchApp: async (id) => {
    let resp = await client.apis['app'].readApp({ id: id })
    return resp.obj.content
  },

  addApp: async (app) => {
    let resp = await client.apis['app'].addApp(
      {},
      {
        requestBody: app,
      }
    )
    return resp.obj.content
  },

  updateApp: async (app) => {
    let resp = await client.apis['app'].updateApp(
      { id: app.id },
      {
        requestBody: app,
      }
    )
    return resp.obj.content
  },

  deleteApp: async (id) => {
    let resp = await client.apis['app'].deleteApp({ id: id })
    return resp.obj
  },

  fetchAppStatusLogs: async (
    opts = {
      page: 0,
      pageSize: 20,
    }
  ) => {
    let resp = await client.apis['app'].readAppStatusLogs(utils.snakelize(opts))
    return resp.obj
  },
}

const edgeFaceDetector = {
  fetchDetectHistory: async (appId, opts = {}) => {
    let resp = await client.apis['edge-face-detector'].readDetectHistory({
      app_id: appId,
      ...utils.snakelize(opts),
    })
    return resp.obj.content
  },
}

const invoice = {
  fetchInvoices: async (
    opts = {
      page: 0,
      pageSize: 20,
    }
  ) => {
    let resp = await client.apis['invoice'].readInvoices({
      page: opts.page,
      page_size: opts.pageSize,
      stime: opts.stime ? opts.stime.toISOString() : null,
      etime: opts.etime ? opts.etime.toISOString() : null,
      sort_by: opts.sortBy,
      sort_direction: opts.sortDirection,
      search_by: opts.searchBy,
      search_text: opts.searchText,
      app_id: opts.appId,
      buyer_name: opts.buyerName,
      payment_type: opts.paymentType,
      type: opts.type,
      status: opts.status,
      is_void: opts.isVoid,
    })
    return resp.obj
  },

  addInvoice: async (invoice) => {
    let resp = await client.apis['invoice'].addInvoice(
      {},
      {
        requestBody: invoice,
      }
    )
    return resp.obj.content
  },

  resetUploadFailureInvoices: async () => {
    let resp = await client.apis['invoice'].resetUploadFailureInvoices(
      {},
      {
        requestBody: {},
      }
    )
    return resp.obj.content
  },
}

const cash = {
  fetchCashs: async (
    opts = {
      page: 0,
      pageSize: 20,
    }
  ) => {
    let resp = await client.apis['cash'].readCashs({
      page: opts.page,
      page_size: opts.pageSize,
      stime: opts.stime ? opts.stime.toISOString() : null,
      etime: opts.etime ? opts.etime.toISOString() : null,
      sort_by: opts.sortBy,
      sort_direction: opts.sortDirection,
      app_id: opts.appId,
      only_problem: opts.onlyProblem,
    })
    return resp.obj
  },

  fetchLastCash: async (appId) => {
    let resp = await client.apis['cash'].readLastCash({
      app_id: appId,
    })
    return resp.obj.content
  },

  addCashs: async (cashs) => {
    let resp = await client.apis['cash'].addCashs(
      {},
      {
        requestBody: cashs,
      }
    )
    return resp.obj.content
  },
}

const system = {
  fetchSystem: async () => {
    let resp = await client.apis['system'].readSystem()
    return resp.obj.content
  },
  fetchSystemRoads: async (opts = {}) => {
    let resp = await client.apis['system'].readSystemRoads(
      utils.snakelize(opts)
    )
    return resp.obj.content
  },
  fetchSystemField: async (field) => {
    let resp = await client.apis['system'].readSystemField({
      field,
    })

    return resp.obj.content
  },
  updateSystemField: async (field, system) => {
    let resp = await client.apis['system'].updateSystemField(
      { field },
      {
        requestBody: system,
      }
    )

    return resp.obj.content
  },
}

const user = {
  addUser: async (user) => {
    let resp = await client.apis['user'].addUser(
      {},
      {
        requestBody: user,
      }
    )

    return resp.obj.content
  },

  updateUser: async (user) => {
    if (!user.id) {
      throw new Error('user id cannot be null')
    }

    let id = user.id
    delete user.id

    let resp = await client.apis['user'].updateUser(
      { id: id },
      {
        requestBody: user,
      }
    )

    return resp.obj.content
  },

  fetchUsers: async (
    opts = {
      page: 0,
      pageSize: 20,
    }
  ) => {
    let resp = await client.apis['user'].readUsers({
      ...utils.snakelize(opts),
    })

    return resp.obj
  },

  fetchUser: async (id) => {
    let resp = await client.apis['user'].readUser({ id: id })

    return resp.obj.content
  },

  deleteUser: async (id) => {
    let resp = await client.apis['user'].deleteUser({ id: id })

    return resp.obj.content
  },

  readUserSalt: async (username) => {
    let resp = await client.apis['user'].readUserSalt({ name: username })
    return resp.obj.content
  },

  loginUser: async (
    form = {
      name: '',
      pass: '',
      salt: '',
    }
  ) => {
    let resp = await client.apis['user'].loginUser(
      {},
      {
        requestBody: {
          name: form.name,
          pass: form.pass,
          csalt: form.salt,
        },
      }
    )

    return resp.obj.content
  },

  login: async (user) => {
    let resp = await client.apis['user'].readUserSalt({
      name: user.name,
    })

    let salt = resp.obj.content.salt
    let csalt = uuid()
    let enpass = crypto.HmacSHA256(user.pass, salt).toString()
    let spass = crypto.HmacSHA256(enpass, csalt).toString()

    resp = await client.apis['user'].loginUser(
      {},
      {
        requestBody: {
          name: user.name,
          pass: spass,
          csalt: csalt,
        },
      }
    )

    return resp.obj.content
  },

  updateUserPicture: async (userId, picture, opts = {}) => {
    opts = utils.snakelize(opts)

    await client.apis['user'].updateUserPicture(
      { id: userId, ...opts },
      {
        requestBody: {
          image: picture,
        },
      }
    )
  },

  fetchWallets: async (userId, opts = {}) => {
    opts = utils.snakelize(opts)

    let resp = await client.apis['user'].readUserWallets({
      id: userId,
      ...opts,
    })

    return resp.obj
  },

  fetchTransactions: async (userId, opts = {}) => {
    opts = utils.snakelize(opts)

    let resp = await client.apis['user'].readUserTransactions({
      id: userId,
      ...opts,
    })

    return resp.obj
  },
}

const store = {
  addStore: async (store) => {
    let resp = await client.apis['store'].addStore(
      {},
      {
        requestBody: store,
      }
    )

    return resp.obj.content
  },
}

const report = {
  fetchMonthReport: async (opts) => {
    let resp = await client.apis['report'].readMonthReport(opts)
    return resp.obj.content
  },

  fetchDailyReport: async () => {
    let resp = await client.apis['report'].readDailyReport()
    return resp.obj.content
  },

  fetchReportDefines: async (opts) => {
    let resp = await client.apis['report'].readReportDefines(opts)
    return resp.obj.content
  },
}

const service = {
  fetchServices: async (opts) => {
    let resp = await client.apis['service'].readServices(utils.snakelize(opts))
    return resp.obj
  },

  fetchServiceRentalNumbers: async () => {
    let resp = await client.apis['service'].readServiceRentalNumbers()
    return resp.obj.content
  },

  fetchService: async (id) => {
    let resp = await client.apis['service'].readService({ id: id })
    return resp.obj.content
  },

  addService: async (service) => {
    let resp = await client.apis['service'].addService(
      {},
      {
        requestBody: service,
      }
    )
    return resp.obj.content
  },

  updateService: async (service) => {
    let id = service.id
    delete service.id
    let resp = await client.apis['service'].updateService(
      { id: id },
      {
        requestBody: service,
      }
    )
    return resp.obj.content
  },

  removeService: async (id) => {
    let resp = await client.apis['service'].removeService({ id: id })
    return resp.obj.content
  },
}

const calendar = {
  fetchCalendar: async (year, month) => {
    if (!year) {
      year = Number(new Date().getFullYear())
    }

    let resp = await client.apis['calendar'].readCalendar({
      year: year,
      month: month,
    })
    return resp.obj.content
  },

  spDay: {
    upsertSpDay: async (day) => {
      let resp = await client.apis['sp-day'].upsertSpDay(
        {},
        {
          requestBody: utils.snakelize(day),
        }
      )
      return resp.obj.content
    },
  },
}

const log = {
  fetchLogs: async (opts = {}) => {
    let resp = await client.apis['log'].readLogs({ ...opts })
    return resp.obj
  },
}

const order = {
  fetchOrders: async (opts) => {
    let resp = await client.apis['order'].readOrders(utils.snakelize(opts))
    return resp.obj
  },

  fetchOrder: async (id) => {
    let resp = await client.apis['order'].readOrder({ id: id })
    return resp.obj.content
  },

  updateOrder: async (order) => {
    let id = order.id
    delete order.id
    let resp = await client.apis['order'].updateOrder(
      { id: id },
      {
        requestBody: order,
      }
    )
    return resp.obj.content
  },

  addOrder: async (order) => {
    let resp = await client.apis['order'].addOrder(
      {},
      {
        requestBody: order,
      }
    )
    return resp.obj.content
  },
}

const agencyReceipt = {
  fetchAgencyReceipts: async (opts) => {
    let resp = await client.apis['agency-receipt'].readAgencyReceipts(
      utils.snakelize(opts)
    )
    return resp.obj
  },
}

const payment = {
  addPayment: async (payment) => {
    let resp = await client.apis['payment'].addPayment(
      {},
      {
        requestBody: payment,
      }
    )
    return resp.obj.content
  },
}

const member = {
  fetchMembers: async (
    opts = {
      page: 0,
      pageSize: 20,
    }
  ) => {
    let resp = await client.apis['member'].readMembers({
      page: opts.page,
      page_size: opts.pageSize,
      search_by: opts.searchBy,
      search_text: opts.searchText,
    })
    return resp.obj
  },
}

const image = {
  uploadImage: async (image, opts = {}) => {
    let resp = await client.apis['image'].uploadImage(utils.snakelize(opts), {
      requestBody: {
        image: image,
      },
    })

    return resp.obj.content
  },

  updateImage: async (imageId, image, opts = {}) => {
    let resp = await client.apis['image'].updateImage(
      { id: imageId, ...utils.snakelize(opts) },
      {
        requestBody: {
          image: image,
        },
      }
    )

    return resp.obj.content
  },
}

const rentalUser = {
  fetchRentalUsers: async (opts) => {
    let resp = await client.apis['rental-user'].readRentalUsers(
      utils.snakelize(opts)
    )
    return resp.obj
  },

  fetchRentalUser: async (id) => {
    let resp = await client.apis['rental-user'].readRentalUser({ id: id })
    return resp.obj.content
  },

  fetchRentalUserItems: async (id, opts = {}) => {
    let resp = await client.apis['rental-user'].readRentalUserItems({
      id: id,
      ...utils.snakelize(opts),
    })
    return resp.obj
  },

  fetchLastRentalUser: async (opts) => {
    console.log(utils.snakelize(opts))
    let resp = await client.apis['rental-user'].readLastRentalUser(
      utils.snakelize(opts)
    )
    return resp.obj
  },

  addRentalUser: async (rentalUser) => {
    let resp = await client.apis['rental-user'].addRentalUser(
      {},
      {
        requestBody: rentalUser,
      }
    )
    return resp.obj.content
  },
  updateRentalUser: async (rentalUser) => {
    let id = rentalUser.id
    delete rentalUser.id

    let resp = await client.apis['rental-user'].updateRentalUser(
      { id: id },
      {
        requestBody: rentalUser,
      }
    )
    return resp.obj.content
  },

  deleteRentalUser: async (id) => {
    let resp = await client.apis['rental-user'].deleteRentalUser({ id: id })
    return resp.obj.content
  },

  readRentalUserCards: async (id, opts = {}) => {
    let resp = await client.apis['rental-user'].readRentalUserCards({
      id: id,
      ...utils.snakelize(opts),
    })
    return resp.obj
  },

  importRentalUsers: async (file) => {
    let resp = await client.apis['rental-user'].importRentalUsers(
      {},
      {
        requestBody: {
          file: file,
        },
      }
    )

    return resp.obj.content
  },

  exportRentalUsers: async (file) => {
    let resp = await client.apis['rental-user'].exportRentalUsers({}, {})

    return resp.obj
  },
}

const rentalGroup = {
  getRentalGroups: async (opts) => {
    let resp = await client.apis['rental-group'].getRentalGroups(
      utils.snakelize(opts)
    )
    return resp.obj
  },
}

const userBlockEvent = {
  getUserBlockEvent: async (opts) => {
    let resp = await client.apis['user-block-event'].getUserBlockEvent(
      utils.snakelize(opts)
    )
    return resp.obj
  },
  getUserBlockEventById: async (opts) => {
    let resp = await client.apis['user-block-event'].getUserBlockEventById(
      utils.snakelize(opts)
    )
    return resp.obj
  },
}

const itemDiscount = {
  addDiscount: async (discount) => {
    let resp = await client.apis['item-discount'].addItemDiscount(
      {},
      {
        requestBody: discount,
      }
    )

    return resp.obj.content
  },
}

const discountDefine = {
  readDiscountDefines: async (opts) => {
    let resp = await client.apis['discount-define'].readDiscountDefines(
      utils.snakelize(opts)
    )

    return resp.obj
  },

  addDiscountDefine: async (body) => {
    let resp = await client.apis['discount-define'].addDiscountDefine(
      {},
      {
        requestBody: body,
      }
    )

    return resp.obj.content
  },

  updateDiscountDefine: async (discountDefine) => {
    let resp = await client.apis['discount-define'].updateDiscountDefine(
      {
        id: discountDefine.id,
      },
      {
        requestBody: discountDefine,
      }
    )

    return resp.obj.content
  },

  readDiscountDefine: async (id) => {
    let resp = await client.apis['discount-define'].readDiscountDefine({
      id,
    })

    return resp.obj.content
  },
}

const checkout = {
  readDailyParkingCheckoutReport: async (opts) => {
    let resp = await client.apis['checkout'].readDailyParkingCheckoutReport(
      utils.snakelize(opts)
    )

    return resp.obj
  },
}

const rentalCard = {
  readRentalCards: async (opts) => {
    let resp = await client.apis['rental-card'].readRentalCards(
      utils.snakelize(opts)
    )

    return resp.obj
  },
  updateRentalCardPlate: async (opts) => {
    let id = opts.id
    delete opts.id

    let resp = await client.apis['rental-card'].updateRentalCardPlate(
      { id: id },
      {
        requestBody: utils.snakelize(opts),
      }
    )

    return resp.obj
  },
}

const carRegistration = {
  readCarRegistrations: async (opts) => {
    let resp = await client.apis['car-registration'].readCarRegistrations(
      utils.snakelize(opts)
    )

    return resp.obj
  },

  readCarRegistration: async (id) => {
    let resp = await client.apis['car-registration'].readCarRegistration({
      id,
    })

    return resp.obj.content
  },

  updateCarRegistration: async (id, reg) => {
    let resp = await client.apis['car-registration'].updateCarRegistration(
      { id },
      {
        requestBody: reg,
      }
    )

    return resp.obj.content
  },

  addCarRegistration: async (reg) => {
    let resp = await client.apis['car-registration'].addCarRegistration(
      {},
      {
        requestBody: reg,
      }
    )

    return resp.obj.content
  },

  importCarRegistrations: async (file) => {
    let resp = await client.apis['car-registration'].importCarRegistrations(
      {},
      {
        requestBody: {
          file: file,
        },
      }
    )

    return resp.obj.content
  },
}

const wallet = {
  deposit: async (id, body) => {
    let resp = await client.apis['wallet'].depositWallet(
      {
        id: id,
      },
      {
        requestBody: body,
      }
    )

    return resp.obj
  },
}

const customerWallet = {
  deposit: async (id, body) => {
    let resp = await client.apis['wallet'].depositCustomerWallet(
      {
        id: id,
      },
      {
        requestBody: body,
      }
    )

    return resp.obj
  },

  withdraw: async (id, body) => {
    let resp = await client.apis['wallet'].withdrawCustomerWallet(
      {
        id: id,
      },
      {
        requestBody: body,
      }
    )

    return resp.obj
  },
}

const customer = {
  readCustomers: async (opts) => {
    let resp = await client.apis['customer'].readCustomers(
      utils.snakelize(opts)
    )

    return resp.obj
  },

  readCustomer: async (id) => {
    let resp = await client.apis['customer'].readCustomer({ id })

    return resp.obj.content
  },

  updateCustomer: async (id, customer) => {
    let resp = await client.apis['customer'].updateCustomer(
      { id },
      {
        requestBody: customer,
      }
    )

    return resp.obj.content
  },

  addCustomer: async (customer) => {
    let resp = await client.apis['customer'].addCustomer(
      {},
      {
        requestBody: customer,
      }
    )

    return resp.obj.content
  },

  importCustomers: async (file) => {
    let resp = await client.apis['customer'].importCustomers(
      {},
      {
        requestBody: {
          file: file,
        },
      }
    )

    return resp.obj.content
  },

  fetchWallets: async (customerId, opts = {}) => {
    opts = utils.snakelize(opts)

    let resp = await client.apis['customer'].readCustomerWallets({
      id: customerId,
      ...opts,
    })

    return resp.obj
  },

  fetchTransactions: async (customerId, opts = {}) => {
    opts = utils.snakelize(opts)

    let resp = await client.apis['customer'].readCustomerTransactions({
      id: customerId,
      ...opts,
    })

    return resp.obj
  },
}

const rentalUserProvider = {
  refreshRentalUser: async (opts) => {
    let resp = await client.apis['rental-user-provider'].refreshRentalUser(
      utils.snakelize(opts)
    )

    return resp.obj.content
  },
}

const blackList = {
  allowBlackUserOnce: async (id) => {
    let resp = await client.apis['black-list'].allowBlackUserOnce(
      {
        id,
      },
      {
        requestBody: {},
      }
    )

    return resp.obj.content
  },
}

const dailyNonSystemRevenue = {
  readDailyNonSystemRevenue: async (opts) => {
    let resp = await client.apis[
      'daily-non-system-revenue'
    ].readDailyNonSystemRevenue(utils.snakelize(opts))

    return resp.obj.content
  },

  uploadDailyNonSystemRevenue: async (body) => {
    let resp = await client.apis[
      'daily-non-system-revenue'
    ].uploadDailyNonSystemRevenue(
      {},
      {
        requestBody: body,
      }
    )

    return resp.obj.content
  },
}

const parkSpace = {
  readParkSpaces: async (opts) => {
    let resp = await client.apis['park-space'].readParkSpaces(
      utils.snakelize(opts)
    )

    return resp.obj
  },
}

const health = {
  readHealth: async (opts) => {
    let resp = await client.apis['health'].readHealth()

    return resp.obj.content
  },
}

const drivewayGateController = {
  raiseGate: async (gatewayId) => {
    let resp = await client.apis['driveway-gate'].raiseGate({ id: gatewayId })
    return resp.obj
  },
  lowerGate: async (gatewayId) => {
    let resp = await client.apis['driveway-gate'].lowerGate({ id: gatewayId })
    return resp.obj
  },
}

const site = {
  readSites: async (opts) => {
    let resp = await client.apis['site'].readSites()

    return resp.obj
  },
}

const pos = {
  init,
  url,
  item,
  category,
  ingredient,
  app,
  invoice,
  cash,
  system,
  user,
  store,
  report,
  service,
  calendar,
  log,
  order,
  agencyReceipt,
  payment,
  member,
  image,
  rentalUser,
  edgeFaceDetector,
  itemDiscount,
  discountDefine,
  checkout,
  rentalCard,
  carRegistration,
  wallet,
  customer,
  rentalUserProvider,
  blackList,
  dailyNonSystemRevenue,
  parkSpace,
  health,
  customerWallet,
  drivewayGateController,
  site,
  rentalGroup,
  userBlockEvent,
}

export default pos
